// ✅ src/pages/VehicleV2/JobCardListing.jsx  (UPDATED FULL FILE)
// Backend expected:
//   app.use("/api/jobCardListing", jobCardListing);
//
// APIs used:
//   GET  http://nissi-india.com/api/jobCardListing/init?loginUserId=..&branchId=..&branchType=..&selectedBranchId=..
//   GET  http://nissi-india.com/api/jobCardListing/list
//   POST http://nissi-india.com/api/jobCardListing/delete
//
// ✅ Updated pattern like BatteryTesting:
// - common localStorage/sessionStorage header pattern
// - useMemo + useCallback pattern
// - loading + pageLoading states
// - same UI structure style
// - no Add New button
// - View + Edit action buttons
// - Edit navigates to /vehicle/JobCardEntry?vehicleId=<id>
// - View navigates to /vehicle/JobCardEntryDetail?vehicleId=<id>

import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaFilter,
  FaTrash,
  FaEye,
  FaEdit,
  FaListAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/jobCardListing";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const JobCardListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ---- UI states ----
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  // dropdown data
  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  // filters
  const [filters, setFilters] = useState({
    selectedBranchId: "Select",
    storeId: "Select",
    vehicleTypeIndex: "Select",
    vehicleRegNo: "",
    vehicleName: "",
    vehicleCode: "",
  });

  const [search, setSearch] = useState("");

  // grid
  const [jobCards, setJobCards] = useState([]);

  // paging
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);
    return t > 0 ? t : 1;
  }, [total, limit]);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // ---- styles ----
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const mapBranchOption = useCallback((b) => {
    return {
      value: toText(b?.value ?? b?.id ?? b?.branchId ?? ""),
      label: toText(b?.label ?? b?.name ?? b?.branchName ?? ""),
    };
  }, []);

  const mapStoreOption = useCallback((s) => {
    return {
      value: toText(s?.value ?? s?.id ?? s?.storeId ?? ""),
      label: toText(s?.label ?? s?.name ?? s?.storeName ?? ""),
    };
  }, []);

  const mapVehicleTypeOption = useCallback((v) => {
    return {
      value: toText(v?.value ?? v?.id ?? v?.vehicleTypeIndex ?? ""),
      label: toText(v?.label ?? v?.name ?? v?.vehicleTypeName ?? ""),
    };
  }, []);

  const mapRow = useCallback((r) => {
    return {
      id: toInt(r?.id ?? r?.jobCardId ?? r?.vehicleId ?? 0),
      vehicleMainType: toText(r?.vehicleMainType ?? r?.fld_vehiclemaintype),
      vehicleCode: toText(r?.vehicleCode ?? r?.fld_vehiclecode),
      vehicleName: toText(r?.vehicleName ?? r?.fld_vehiclename),
      vehiclePortNo: toText(
        r?.vehiclePortNo ?? r?.vehicleRegNo ?? r?.fld_vehicleportno
      ),
      jobCardNo: toText(r?.jobCardNo ?? r?.fld_jobcardno),
      status: toText(r?.status ?? r?.fld_status),
      location: toText(r?.location ?? r?.fld_location),
    };
  }, []);

  /* ------------------------- INIT LOAD ------------------------- */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          branchType: clean(branchType),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      const branchOptions = [
        { value: "Select", label: "Select" },
        ...(Array.isArray(data?.branches) ? data.branches.map(mapBranchOption) : []),
      ];

      const storeOptions = [
        { value: "Select", label: "Select" },
        ...(Array.isArray(data?.stores) ? data.stores.map(mapStoreOption) : []),
      ];

      const vehicleTypeOptions = [
        { value: "Select", label: "Select" },
        ...(Array.isArray(data?.vehicleTypes)
          ? data.vehicleTypes.map(mapVehicleTypeOption)
          : []),
      ];

      setBranches(branchOptions);
      setStores(storeOptions);
      setVehicleTypes(vehicleTypeOptions);

      if (clean(branchType).toUpperCase() !== "HO") {
        const myBranch = branchOptions.find(
          (b) => String(b.value) === String(branchId)
        );

        setFilters((prev) => ({
          ...prev,
          selectedBranchId: myBranch ? myBranch.value : String(branchId),
        }));
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Init failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    branchType,
    commonHeaders,
    mapBranchOption,
    mapStoreOption,
    mapVehicleTypeOption,
  ]);

  /* ------------------------- STORE LOAD ------------------------- */
  const loadStoresForBranch = useCallback(
    async (branchVal) => {
      try {
        const { data } = await axios.get(`${API_BASE}/init`, {
          params: {
            loginUserId: toInt(loginUserId, 0),
            branchId: toInt(branchId, 0),
            branchType: clean(branchType),
            selectedBranchId: branchVal !== "Select" ? branchVal : "",
          },
          headers: commonHeaders,
        });

        if (!data?.success) return;

        const storeOptions = [
          { value: "Select", label: "Select" },
          ...(Array.isArray(data?.stores) ? data.stores.map(mapStoreOption) : []),
        ];

        setStores(storeOptions);
      } catch (err) {
        console.error(err);
      }
    },
    [loginUserId, branchId, branchType, commonHeaders, mapStoreOption]
  );

  /* ------------------------- LIST LOAD ------------------------- */
  const loadList = useCallback(
    async (nextPage = page, nextLimit = limit, overrideFilters = null) => {
      try {
        setLoading(true);

        const f = overrideFilters || filters;

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            loginUserId: toInt(loginUserId, 0),
            branchId: toInt(branchId, 0),
            branchType: clean(branchType),
            selectedBranchId:
              f.selectedBranchId !== "Select" ? f.selectedBranchId : "",
            storeId: f.storeId !== "Select" ? f.storeId : "",
            vehicleTypeIndex:
              f.vehicleTypeIndex !== "Select" ? f.vehicleTypeIndex : "",
            vehicleRegNo: clean(f.vehicleRegNo),
            vehicleName: clean(f.vehicleName),
            vehicleCode: clean(f.vehicleCode),
            search: clean(search),
            page: toInt(nextPage, 1),
            limit: toInt(nextLimit, 10),
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        const list = Array.isArray(data?.list) ? data.list.map(mapRow) : [];

        setJobCards(list);
        setTotal(toInt(data?.total, 0));
        setPage(toInt(data?.page, nextPage));
        setLimit(toInt(data?.limit, nextLimit));

        setSelectedIds([]);
        setSelectAll(false);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load list"
        );
        setJobCards([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      limit,
      filters,
      loginUserId,
      branchId,
      branchType,
      search,
      commonHeaders,
      mapRow,
    ]
  );

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    loadList(page, limit);
  }, [page, limit, loadList]);

  // keep select all state synced
  useEffect(() => {
    const idsOnPage = jobCards.map((x) => x.id).filter(Boolean);
    setSelectAll(
      idsOnPage.length > 0 && idsOnPage.every((id) => selectedIds.includes(id))
    );
  }, [selectedIds, jobCards]);

  /* ------------------------- SELECTION ------------------------- */
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }

    const ids = jobCards.map((x) => x.id).filter(Boolean);
    setSelectedIds(ids);
    setSelectAll(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ------------------------- ACTIONS ------------------------- */
  const handleSearchClick = () => {
    setPage(1);
    loadList(1, limit);
  };

  const handleViewAll = () => {
    const cleared = {
      selectedBranchId:
        clean(branchType).toUpperCase() !== "HO" ? String(branchId) : "Select",
      storeId: "Select",
      vehicleTypeIndex: "Select",
      vehicleRegNo: "",
      vehicleName: "",
      vehicleCode: "",
    };

    setSearch("");
    setFilters(cleared);
    setPage(1);
    loadList(1, limit, cleared);
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one job card to delete!");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds,
          loginUserId: toInt(loginUserId, 0),
        },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      if ((data?.blocked || []).length > 0) {
        toast.warn(
          `Deleted: ${data?.deleted || 0}. Blocked (in use): ${
            data.blocked.length
          }`
        );
      } else {
        toast.success(data?.message || "Deleted successfully");
      }

      await loadList(page, limit);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Delete failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleView = (row) => {
    const id = row?.id;
    if (!id) {
      toast.error("Id not found for this row");
      return;
    }
    navigate(`/vehicle/JobCardEntryDetail?vehicleId=${encodeURIComponent(id)}`);
  };

  const handleEdit = (row) => {
    const id = row?.id;
    if (!id) {
      toast.error("Id not found for this row");
      return;
    }
    navigate(`/vehicle/JobCardEntry?vehicleId=${encodeURIComponent(id)}`);
  };

  const paginationText = useMemo(() => {
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(total, page * limit);
    return `${from}-${to} of ${total}`;
  }, [page, limit, total]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">JOB CARD</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Job Card Listing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                }`}
                disabled={loading || pageLoading}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Branch */}
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    value={filters.selectedBranchId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFilters((prev) => ({
                        ...prev,
                        selectedBranchId: val,
                        storeId: "Select",
                      }));

                      if (clean(branchType).toUpperCase() === "HO") {
                        loadStoresForBranch(val);
                      }
                    }}
                    className={inputClass}
                    disabled={clean(branchType).toUpperCase() !== "HO"}
                  >
                    {branches.map((b, idx) => (
                      <option key={idx} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Type */}
                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <select
                    value={filters.vehicleTypeIndex}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        vehicleTypeIndex: e.target.value,
                      }))
                    }
                    className={inputClass}
                  >
                    {vehicleTypes.map((t, idx) => (
                      <option key={idx} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Store */}
                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={filters.storeId}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        storeId: e.target.value,
                      }))
                    }
                    className={inputClass}
                  >
                    {stores.map((s, idx) => (
                      <option key={idx} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Reg No */}
                <div>
                  <label className={labelClass}>Vehicle Reg. No</label>
                  <input
                    type="text"
                    value={filters.vehicleRegNo}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        vehicleRegNo: e.target.value,
                      }))
                    }
                    className={inputClass}
                  />
                </div>

                {/* Vehicle Name */}
                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={filters.vehicleName}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        vehicleName: e.target.value,
                      }))
                    }
                    className={inputClass}
                  />
                </div>

                {/* Vehicle Code */}
                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={filters.vehicleCode}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        vehicleCode: e.target.value,
                      }))
                    }
                    className={inputClass}
                  />
                </div>

                {/* Search */}
                <div className="md:col-span-3">
                  <label className={labelClass}>
                    Search (JobCard / Vehicle / Code)
                  </label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={inputClass}
                    placeholder="Type and press Search"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleViewAll}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                  disabled={loading || pageLoading}
                >
                  Clear / View All
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={handleSearchClick}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                    disabled={loading || pageLoading}
                  >
                    <FaSearch />
                    Search
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {pageLoading ? (
              <div className="text-center py-16">Loading...</div>
            ) : jobCards.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaListAlt className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Job Card Records Found
                </h3>
                <p className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try changing filters or search criteria.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-[1200px] w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">
                        S.No
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Main Type
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Reg No
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Job Card No
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Status
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Location
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Actions
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        <div className="flex items-center justify-center">
                          Select All
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="ml-2"
                            disabled={loading}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan={10} className="text-center py-10">
                          Loading...
                        </td>
                      </tr>
                    ) : (
                      jobCards.map((row, idx) => (
                        <tr
                          key={row.id ?? idx}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-900" : "bg-white"
                          }`}
                        >
                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {(page - 1) * limit + idx + 1}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleMainType}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleCode}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleName}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehiclePortNo}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.jobCardNo}
                          </td>

                          <td className="px-4 py-3 text-sm text-center">
                            {row.status ? (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                {row.status}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.location}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleView(row)}
                                className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                                disabled={loading}
                              >
                                <FaEye /> View
                              </button>

                              <button
                                onClick={() => handleEdit(row)}
                                className={`inline-flex items-center gap-1 px-3 py-2 ${buttonClass} ${hoverClass} text-white text-xs font-semibold rounded-lg transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg`}
                                disabled={loading}
                              >
                                <FaEdit /> Edit
                              </button>
                            </div>
                          </td>

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(row.id)}
                              onChange={() => toggleSelect(row.id)}
                              disabled={loading}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
            <div
              className={`text-sm ${
                isDark ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {paginationText}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                disabled={page <= 1 || loading || pageLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={`px-4 py-2 rounded-lg border ${
                  page > 1 && !loading && !pageLoading
                    ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                    : "opacity-50 cursor-not-allowed"
                } ${
                  isDark
                    ? "border-gray-700 text-gray-200"
                    : "border-gray-300 text-gray-700"
                }`}
              >
                Prev
              </button>

              <div
                className={`text-sm px-2 ${
                  isDark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                Page {page} / {totalPages}
              </div>

              <button
                disabled={page >= totalPages || loading || pageLoading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className={`px-4 py-2 rounded-lg border ${
                  page < totalPages && !loading && !pageLoading
                    ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                    : "opacity-50 cursor-not-allowed"
                } ${
                  isDark
                    ? "border-gray-700 text-gray-200"
                    : "border-gray-300 text-gray-700"
                }`}
              >
                Next
              </button>

              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                disabled={loading || pageLoading}
                className={inputClass}
                style={{ width: 120 }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Delete */}
          <div className="flex justify-end mt-6">
            <button
              onClick={handleDelete}
              className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                selectedIds.length === 0 || loading || pageLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : `${buttonClass} ${hoverClass}`
              }`}
              disabled={selectedIds.length === 0 || loading || pageLoading}
            >
              <FaTrash /> Delete Selected
            </button>
          </div>

          <div
            className={`mt-8 pt-6 border-t ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <p
              className={`text-sm text-center ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              INFRA SCREEN : JOB CARD LISTING
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default JobCardListing;